import React, { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardContent } from '@/components/ui/Card';
import { CameraCapture } from '@/components/camera/CameraCapture';
import { classNames, formatPercent } from '@/lib/utils';
import type { Segment, Measurement } from '@/types';

interface SegmentFormProps {
    segment: Segment;
    initialValue?: Partial<Measurement>;
    onSubmit: (data: { measuredShare: number; linearCm?: number; imageBlob?: Blob; imageUrl?: string }) => void;
    onBack?: () => void;
}

export function SegmentForm({ segment, initialValue, onSubmit, onBack }: SegmentFormProps) {
    const [measuredShare, setMeasuredShare] = useState(initialValue?.measuredShare?.toString() || '');
    const [linearCm, setLinearCm] = useState(initialValue?.linearCm?.toString() || '');
    const [capturedImage, setCapturedImage] = useState<{ blob: Blob; url: string } | null>(null);
    const [showCamera, setShowCamera] = useState(false);
    const [error, setError] = useState('');

    const shareValue = parseFloat(measuredShare) || 0;
    const isCompliant = shareValue >= segment.targetShare;

    const handleCapture = (blob: Blob, url: string) => {
        setCapturedImage({ blob, url });
        setShowCamera(false);
    };

    const handleSubmit = () => {
        setError('');

        if (!measuredShare || isNaN(parseFloat(measuredShare))) {
            setError('Informe a porcentagem de share');
            return;
        }

        const share = parseFloat(measuredShare);
        if (share < 0 || share > 100) {
            setError('A porcentagem deve estar entre 0 e 100');
            return;
        }

        onSubmit({
            measuredShare: share,
            linearCm: linearCm ? parseFloat(linearCm) : undefined,
            imageBlob: capturedImage?.blob,
            imageUrl: capturedImage?.url,
        });
    };

    // Tela de câmera em fullscreen
    if (showCamera) {
        return (
            <div className="fixed inset-0 z-50 bg-black">
                <CameraCapture
                    onCapture={handleCapture}
                    onCancel={() => setShowCamera(false)}
                />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header do segmento */}
            <Card>
                <CardContent>
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-xl font-semibold text-content-title">
                                {segment.name}
                            </h2>
                            <p className="text-sm text-content-muted">
                                {segment.category}
                            </p>
                        </div>
                        <div className="text-right">
                            <p className="text-sm text-content-muted">Meta</p>
                            <p className="text-2xl font-bold text-brand-primary">
                                {formatPercent(segment.targetShare, 0)}
                            </p>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Formulário */}
            <Card>
                <CardContent className="space-y-4">
                    {/* Porcentagem medida */}
                    <div>
                        <label className="block text-sm font-medium text-content-title mb-2">
                            Share Medido (%)
                        </label>
                        <div className="flex items-center gap-3">
                            <input
                                type="range"
                                min="0"
                                max="100"
                                step="1"
                                value={shareValue}
                                onChange={(e) => setMeasuredShare(e.target.value)}
                                className="flex-1 h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-brand-primary"
                            />
                            <input
                                type="number"
                                min="0"
                                max="100"
                                value={measuredShare}
                                onChange={(e) => setMeasuredShare(e.target.value)}
                                placeholder="0"
                                className="w-20 px-3 py-2 text-center text-lg font-semibold border border-ui-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                            />
                        </div>

                        {/* Indicador de compliance */}
                        {measuredShare && (
                            <div className={classNames(
                                'mt-3 p-3 rounded-lg flex items-center gap-2',
                                isCompliant ? 'bg-status-success/10' : 'bg-status-danger/10'
                            )}>
                                {isCompliant ? (
                                    <>
                                        <svg className="w-5 h-5 text-status-success" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                        <span className="text-sm font-medium text-status-success">
                                            Em compliance! ({formatPercent(shareValue - segment.targetShare, 1)} acima da meta)
                                        </span>
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-5 h-5 text-status-danger" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                        </svg>
                                        <span className="text-sm font-medium text-status-danger">
                                            Abaixo da meta ({formatPercent(segment.targetShare - shareValue, 1)} faltando)
                                        </span>
                                    </>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Medida linear (opcional) */}
                    <Input
                        type="number"
                        label="Medida Linear (cm) - Opcional"
                        placeholder="Ex: 120"
                        value={linearCm}
                        onChange={(e) => setLinearCm(e.target.value)}
                    />

                    {/* Foto */}
                    <div>
                        <label className="block text-sm font-medium text-content-title mb-2">
                            Foto da Gôndola
                        </label>

                        {capturedImage ? (
                            <div className="relative rounded-lg overflow-hidden">
                                <img
                                    src={capturedImage.url}
                                    alt="Foto capturada"
                                    className="w-full h-48 object-cover"
                                />
                                <button
                                    onClick={() => setCapturedImage(null)}
                                    className="absolute top-2 right-2 p-2 bg-black/50 rounded-full text-white"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        ) : (
                            <button
                                onClick={() => setShowCamera(true)}
                                className="w-full h-32 border-2 border-dashed border-ui-border rounded-lg flex flex-col items-center justify-center gap-2 text-content-muted hover:border-brand-primary hover:text-brand-primary transition-colors"
                            >
                                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                <span className="text-sm">Tirar Foto</span>
                            </button>
                        )}
                    </div>

                    {error && (
                        <p className="text-sm text-status-danger">{error}</p>
                    )}
                </CardContent>
            </Card>

            {/* Botões */}
            <div className="flex gap-3">
                {onBack && (
                    <Button variant="secondary" size="lg" fullWidth onClick={onBack}>
                        Voltar
                    </Button>
                )}
                <Button variant="primary" size="lg" fullWidth onClick={handleSubmit}>
                    Próximo
                </Button>
            </div>
        </div>
    );
}

export default SegmentForm;
